import {AfterViewInit, Component, Input, ViewChild, ViewEncapsulation} from '@angular/core';
import {ModelID} from "../../_typing/generic-types";
import {ComponentDataService} from "../../data/component-data.service";
import {AuditMixin} from "../../_models/audit-mixin";
import {BaseComponent} from "../../shared/base.component";
import {MatTableDataSource} from "@angular/material/table";
import {ComponentComponent} from "../../_models/component-component";
import {forkJoin, Observable, of, Subject} from "rxjs";
import {catchError, map, take} from "rxjs/operators";
import {ApiService} from "../../services/api/api.service";
import {MatSort} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {UtilsBarComponent} from "../utils-bar/utils.bar.component";
import {COMMON_TABLE_IMPORTS} from "../../shared/import-groups/Common";
import {StringDatePipe} from "../../shared/pipes";


@Component({
    selector: 'linked-component-audit-table',
    templateUrl: './linked-component-audit-table.component.html',
    styleUrl: './linked-component-audit-table.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [
        UtilsBarComponent,
        COMMON_TABLE_IMPORTS,
        StringDatePipe
    ]
})
export class LinkedComponentAuditTableComponent extends BaseComponent {
    @Input() config: { componentId: ModelID, componentTypeId: ModelID, relatedComponentTypeId: ModelID }
    @Input() model: AuditMixin & { id: string; type: string; auditItemId?: ModelID };
    columns: string[] = ['name', 'created_by', 'created_on'];
    componentId: ModelID;
    componentComponents: ComponentComponent[];
    dataSource: MatTableDataSource<any>
    pageSize = 5;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(private componentDataService: ComponentDataService,
                private api: ApiService) {
        super();
    }

    ngOnInit() {
        this.componentId = this.model.id;
        this.componentComponents = this.model.relationships?.['data'];

        let data = [];
        this.componentComponents.forEach(cc => {
            const relComponentId = cc.relationships.first_component.data.id === this.componentId ? cc.relationships.second_component.data.id : cc.relationships.first_component.data.id;

            const item = {
                id: relComponentId,
                created_by: `${cc.attributes.created_by_name} ${cc.attributes.created_by_lastname !== null ? cc.attributes.created_by_lastname : ""}`,
                created_on: cc.attributes.created_on,
                changed_by: `${cc.attributes.changed_by_name} ${cc.attributes.changed_by_lastname !== null ? cc.attributes.changed_by_lastname : ""}`,
                changed_on: cc.attributes.changed_on
            }
            data.push(item);
        })

        const idsToResolve = data.map(item => item.id);

        forkJoin(idsToResolve.map(id =>
                this._getComponentName(id).pipe(
                    catchError(() => of('Unknown'))
                )
            )
        ).pipe(take(1)).subscribe(names => {
            const enrichedData = data.map((item, i) => ({...item, name: names[i]}));
            this.dataSource = new MatTableDataSource(enrichedData);
            this._addSortAndFilter();
            this.dataSource.sort = this.sort;
            this.dataSource.paginator = this.paginator;
        });

    }

    private _getComponentName(id: string): Observable<string> {
        return this.api.component.getName(id);
    }

    clearSearch() {
        this.dataSource.filter = '';
        this.paginator.pageIndex = 0;
    }

    private _addSortAndFilter() {
        this.dataSource.filterPredicate = (data, filter) => {
            if (!filter) return data;
            const normalized = filter.toLowerCase();
            return (
                data.changed_by.toLowerCase().includes(normalized) ||
                data.created_by.toLowerCase().includes(normalized) ||
                data.name?.toLowerCase().includes(normalized)
            );
        };

        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'name':
                    return item.name?.toLowerCase() || '';
                case 'created_by':
                    return item.created_by?.toLowerCase() || '';
                case 'changed_by':
                    return item.changed_by?.toLowerCase() || '';
                case 'created_on':
                    return new Date(item.created_on).getTime(); // or use moment if needed
                case 'changed_on':
                    return new Date(item.changed_on).getTime();
                default:
                    return item[property];
            }
        };
    }

    applyFilter(filterValue: any) {
        filterValue = filterValue.trim().toLowerCase();
        this.dataSource.filter = filterValue;
    }
}
